// imports
import { FC, memo, useCallback, useMemo, useState } from 'react';
// components
import InfiniteSelect from './InfiniteSelect';
// interfaces, constants, graphql
import { HTTP_STATUS } from 'constants/index';
import { renderExcipientType } from 'lib/helper';
import { ExcipientTypeSelectProps } from 'interfaces';
import { ExcipientTypesPayload, useFindAllExcipientTypesSelectorLazyQuery } from 'generated/graphql';

const ExcipientTypeSelect: FC<ExcipientTypeSelectProps> = ({
  name,
  title,
  value,
  isActive,
  handleChange,
  margin = 'dense',
  isClearable = false,
  productType,
  excipientLabelId,
  ...rest
}) => {
  const [totalPages, setTotalPages] = useState(0);
  const [getLoading, setGetLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<ExcipientTypesPayload['data']>([]);

  const updatedOptions = useMemo(() => {
    if (options?.length) {
      return renderExcipientType(options);
    }
    return [];
  }, [options]);

  const [fetchSelectorData, { loading }] = useFindAllExcipientTypesSelectorLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { findAllExcipientTypes } = data;
      const { data: excipientTypes, response, pagination } = findAllExcipientTypes || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { totalPages: pages } = pagination || {};
        setTotalPages(pages);
        setOptions([
          ...(getLoading ? [] : options || []),
          ...((excipientTypes as ExcipientTypesPayload['data']) ?? []),
        ]);
        setGetLoading(false);
      } else {
        setGetLoading(false);
      }
    },
    onError: () => {
      setOptions([]);
      setGetLoading(false);
    },
  });

  const fetchData = useCallback(
    async (page?: number, query?: string) => {
      if (!page) setGetLoading(true);
      await fetchSelectorData({
        variables: {
          findAllExcipientTypesInput: {
            ...(query && { search: query }),
            paginationOptions: { page: page || 1, limit: 10 },
            ...(typeof isActive === 'boolean' && { isActive }),
            ...(productType && { productType }),
            ...(excipientLabelId && { excipientLabelId }),
          },
        },
      });
    },
    [fetchSelectorData, isActive, productType, excipientLabelId],
  );

  return (
    <InfiniteSelect
      {...rest}
      name={name}
      title={title}
      value={value}
      margin={margin}
      loading={loading}
      fetchData={fetchData}
      setOptions={setOptions}
      totalPages={totalPages}
      getLoading={getLoading}
      options={updatedOptions}
      isClearable={isClearable}
      handleChange={handleChange}
    />
  );
};

export default memo(ExcipientTypeSelect);
